import React, { useState, useEffect, memo, useMemo } from "react";
import { PageContainer } from "../components/Container/PageContainer";
import { getAllCollection } from "../utils/pureDataFetch";
import { Box } from "@mui/material";
import { CardCollectionBook } from "../components/Card/CardCollectionBook";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../hooks/useIsMobile";
import { AllCollectionHeroSection } from "../template/allCollection/AllCollectionHeroSection";
import FadeInGrid from "../components/patterns/motion/FadeInGrid";

const AllCollection = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile("xl");
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const gap = isMobile ? 4 : 16; // 모바일에서 간격 축소

  // 다이나믹 그리드 크기 패턴 (PC 버전에서만 적용)
  const getDynamicGridSize = (index) => {
    if (isMobile) {
      return {
        xs: 6, // 2x2 그리드
        sm: 6, // 2x2 그리드
        md: 4,
        lg: 3,
      };
    }

    // PC 버전 다이나믹 패턴 (6, 3 사이즈만 사용)
    const patterns = [
      // 첫 번째 줄: 6 + 3 + 3 = 12
      { lg: 6, md: 6 }, // 큰 카드
      { lg: 3, md: 3 }, // 작은 카드
      { lg: 3, md: 3 }, // 작은 카드

      // 두 번째 줄: 3 + 6 + 3 = 12
      { lg: 3, md: 3 }, // 작은 카드
      { lg: 6, md: 6 }, // 큰 카드
      { lg: 3, md: 3 }, // 작은 카드

      // 세 번째 줄: 3 + 3 + 6 = 12
      { lg: 3, md: 3 }, // 작은 카드
      { lg: 3, md: 3 }, // 작은 카드
      { lg: 6, md: 6 }, // 큰 카드
    ];

    // 패턴을 반복 적용
    const patternIndex = index % patterns.length;
    return {
      xs: 12,
      sm: 6,
      ...patterns[patternIndex],
    };
  };

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        setLoading(true);
        const allCollections = await getAllCollection();
        setCollections(allCollections);
      } catch (error) {
        console.error("Error fetching all collections:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, []);

  // 메모이제이션으로 필터링 최적화 - 무거운 문장 파싱 로직 제거
  const filteredCollections = useMemo(() => {
    return collections
      .filter((collection) => collection.title && collection.title.length > 0)
      .sort((a, b) => {
        // lastUpdated가 있으면 우선 사용, 없으면 createdAt 사용
        const aDate = new Date(a.lastUpdated || a.createdAt);
        const bDate = new Date(b.lastUpdated || b.createdAt);
        return bDate - aDate;
      });
  }, [collections]);

  return (
    <>
      <AllCollectionHeroSection
        titleKey="allCollection.title"
        descriptionKey="allCollection.description"
        collections={filteredCollections}
      />
      <PageContainer expand={true} sx={{ marginTop: isMobile ? 'calc(-30vh - 80px)' : 'calc(-30vh - 96px)', zIndex: 1000 }}>
        <Box sx={{ width: "100%", maxWidth: "100%" }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "2rem",
              }}
            >
              로딩 중...
            </div>
          ) : (
            <Box sx={{ width: "100%", overflow: "hidden"}}>
              <FadeInGrid
                components={filteredCollections.map((collection, index) => (
                  <CardCollectionBook
                    key={collection.id}
                    collection={collection}
                    isMe={false}
                    isMobile={isMobile}
                    size={
                      isMobile
                        ? "default"
                        : getDynamicGridSize(index).lg === 6
                        ? "large"
                        : "default"
                    }
                  />
                ))}
                itemSize={(index) => getDynamicGridSize(index)}
                container
                spacing={isMobile ? 1 : gap / 8}
                direction="bottom"
                offset={0}
                duration={0.6}
                delay={0}
                once={true}
                amount={0.1}
                sx={{
                  width: "100%",
                  margin: 0,
                  maxWidth: "100%",
                  "& > .MuiGrid-item": {
                    paddingLeft: isMobile ? "4px" : "8px",
                    paddingTop: isMobile ? "4px" : "8px",
                    maxWidth: "100%",
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </PageContainer>
    </>
  );
};

export default AllCollection;
